import { ChannelCell } from '@/components/channel-cell'
import { channelLabel } from '@/components/channel-tag'
import { Check } from '@/components/icons'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuFilterHeader,
  DropdownMenuFilterTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import type { Channel } from '@/lib/vision/schemas'
import { useMemo } from 'react'

/**
 * Marketplace families Pulse always lists. Vision `GET /v1/channels` is the
 * live directory — locally V20 only seeded `amazon_now`, so without this
 * fallback the dropdown is All + Amazon Now.
 */
const ALWAYS_FAMILIES = [
  'amazon',
  'flipkart',
  'flipkart_minutes',
  'myntra',
  'blinkit',
  'ajio',
  'bbnow',
  'instamart',
  'offline_b2b',
] as const
const HIDDEN_FAMILIES = new Set(['hyperpure'])

export interface ChannelFamilyOption {
  id: string
  displayName: string
  logoUrl: string | null
}

export function channelFamilyOptions(channels: Channel[]): ChannelFamilyOption[] {
  const byId = new Map<string, ChannelFamilyOption>()
  for (const row of channels) {
    const id = (row.channel || row.code).trim().toLowerCase()
    if (!id || HIDDEN_FAMILIES.has(id) || byId.has(id)) continue
    byId.set(id, {
      id,
      displayName: channelLabel(id),
      logoUrl: row.logoUrl ?? null,
    })
  }
  for (const id of ALWAYS_FAMILIES) {
    if (!byId.has(id)) {
      byId.set(id, { id, displayName: channelLabel(id), logoUrl: null })
    }
  }
  const rest = [...byId.keys()]
    .filter(id => !ALWAYS_FAMILIES.includes(id as (typeof ALWAYS_FAMILIES)[number]))
    .sort()
  return [...ALWAYS_FAMILIES, ...rest].map(id => byId.get(id)!)
}

/**
 * Multi-select channel dropdown above the Purchase Orders table. Each family
 * is written as a repeated `?channel=` param so the URL remains shareable.
 */
export function MultiChannelFilter({
  value,
  channels,
  loading,
  error,
  onChange,
}: {
  value: string[]
  channels: Channel[]
  loading: boolean
  error: boolean
  onChange: (next: string[]) => void
}) {
  const options = useMemo(() => channelFamilyOptions(channels), [channels])
  const toggle = (option: string) =>
    onChange(value.includes(option) ? value.filter(entry => entry !== option) : [...value, option])

  return (
    <DropdownMenu>
      <DropdownMenuFilterTrigger
        label="Channel"
        loading={loading}
        aria-label="Filter orders by channel"
        title={error ? "Couldn't load the list. Showing the last selection." : undefined}
      >
        {value.length > 0 ? (
          <Badge tone="info" className="mr-1.5 tabular-nums">
            {value.length}
          </Badge>
        ) : (
          <span className="mr-1.5 text-[color:var(--foreground)]">Any</span>
        )}
      </DropdownMenuFilterTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        <DropdownMenuFilterHeader onClear={value.length > 0 ? () => onChange([]) : undefined}>
          Filter by channel
        </DropdownMenuFilterHeader>
        <DropdownMenuSeparator />
        {options.map(option => (
          <DropdownMenuCheckboxItem
            key={option.id}
            checked={value.includes(option.id)}
            onCheckedChange={() => toggle(option.id)}
            onSelect={event => event.preventDefault()}
          >
            <span className="inline-flex min-w-0 items-center gap-2">
              <ChannelCell channel={option.id} logoUrl={option.logoUrl} size={14} />
              <span className="truncate">{option.displayName}</span>
            </span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Single-select variant retained for workflows whose APIs accept one channel. */
export function ChannelFilter({
  value,
  channels,
  loading,
  error,
  onChange,
}: {
  value: string | null
  channels: Channel[]
  loading: boolean
  error: boolean
  onChange: (next: string | null) => void
}) {
  const options = useMemo(() => channelFamilyOptions(channels), [channels])
  const selected = useMemo(() => options.find(option => option.id === value), [options, value])
  const currentLabel = selected?.displayName ?? (value && !loading ? channelLabel(value) : 'All')

  return (
    <DropdownMenu>
      <DropdownMenuFilterTrigger
        label="Channel"
        loading={loading}
        aria-label="Filter orders by channel"
        title={error ? "Couldn't load the list. Showing the last selection." : undefined}
      >
        <span className="mr-1.5 inline-flex max-w-[10rem] items-center gap-1.5 truncate text-[color:var(--foreground)]">
          {selected ? (
            <ChannelCell channel={selected.id} logoUrl={selected.logoUrl} size={14} />
          ) : null}
          <span className="truncate">{currentLabel}</span>
        </span>
      </DropdownMenuFilterTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        <DropdownMenuLabel>Channel</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="justify-between" onSelect={() => onChange(null)}>
          <span>All</span>
          {value == null && <Check className="size-3.5 text-[color:var(--primary)]" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {options.map(option => (
          <DropdownMenuItem
            key={option.id}
            className="justify-between"
            onSelect={() => onChange(option.id)}
          >
            <span className="inline-flex min-w-0 items-center gap-2">
              <ChannelCell channel={option.id} logoUrl={option.logoUrl} size={14} />
              <span className="truncate">{option.displayName}</span>
            </span>
            {option.id === value && <Check className="size-3.5 text-[color:var(--primary)]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
